import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Plus,
  Play,
  Trash2,
  Clock,
  Music,
  Loader2,
  Volume2,
  FolderOpen,
  Search,
  MoreVertical,
  Lock,
  Crown,
  AlertTriangle,
  FileStack,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ProjectTemplateSelector } from '../components/ProjectTemplates';

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { getFeatureLimit, isPro, isStudio, planName } = useSubscription();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  
  // Get project limit from subscription
  const maxProjects = getFeatureLimit('max_projects') || 3;
  const isAtLimit = maxProjects !== -1 && projects.length >= maxProjects;
  const projectsRemaining = maxProjects === -1 ? 'Unlimited' : Math.max(0, maxProjects - projects.length);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/studio/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const deleteProject = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/studio/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setProjects(projects.filter(p => p.id !== projectId));
        toast.success('Project deleted');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const openProject = (projectId) => {
    navigate(`/studio?project=${projectId}`);
  };

  const createNewProject = () => {
    if (isAtLimit) {
      toast.error(
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span>Project limit reached ({maxProjects} projects)</span>
          </div>
          <button 
            onClick={() => navigate('/pricing')}
            className="px-3 py-1.5 bg-amber-500 text-black text-sm rounded font-medium hover:bg-amber-400 transition-colors"
          >
            Upgrade to Pro for unlimited projects
          </button>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    // Show template selector instead of navigating directly
    setShowTemplateSelector(true);
  };
  
  const handleSelectTemplate = (template) => {
    setShowTemplateSelector(false);
    // Navigate to studio with template ID in URL params
    navigate(`/studio?template=${template.id}`);
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white noise-bg">
      {/* Header */}
      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
                <Volume2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">String Sauce</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {/* Project Count / Limit */}
              {maxProjects !== -1 && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                  isAtLimit ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400' : 'bg-zinc-800 text-zinc-400'
                }`}>
                  <FolderOpen className="w-4 h-4" />
                  <span>{projects.length} / {maxProjects}</span>
                  {isAtLimit && <Lock className="w-3 h-3 text-amber-500" />}
                </div>
              )}
              
              {/* Go to Studio - quick access */}
              <Button
                onClick={() => navigate('/studio')}
                variant="outline"
                className="rounded-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                data-testid="go-to-studio-btn"
              >
                <Music className="w-4 h-4 mr-2" />
                Studio
              </Button>
              
              <Button
                onClick={createNewProject}
                className={`rounded-full ${
                  isAtLimit 
                    ? 'bg-amber-500 hover:bg-amber-600 text-black' 
                    : 'bg-indigo-500 hover:bg-indigo-600'
                }`}
                data-testid="new-project-btn"
              >
                {isAtLimit ? (
                  <>
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Your Projects</h1>
            <p className="text-zinc-400">
              {projects.length} project{projects.length !== 1 ? 's' : ''}
              {maxProjects !== -1 && (
                <span className="ml-2">
                  • {projectsRemaining} remaining on {planName} plan
                </span>
              )}
            </p>
            {isAtLimit && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-300">
                <Crown className="w-4 h-4 text-amber-500" />
                <span>Upgrade to Pro for unlimited projects and premium features</span>
                <Button
                  size="sm"
                  onClick={() => navigate('/pricing')}
                  className="ml-auto bg-amber-500 hover:bg-amber-400 text-black"
                >
                  View Plans
                </Button>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-sm focus:border-indigo-500 focus:outline-none"
              data-testid="search-projects"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && projects.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-2xl bg-zinc-800/50 flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-10 h-10 text-zinc-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No projects yet</h2>
            <p className="text-zinc-400 mb-6">Create your first project to get started</p>
            <Button
              onClick={createNewProject}
              className="bg-indigo-500 hover:bg-indigo-600 rounded-full px-6"
              data-testid="create-first-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </motion.div>
        )}

        {/* Projects Grid */}
        {!isLoading && filteredProjects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-all duration-300"
              >
                {/* Project Preview */}
                <div 
                  className="aspect-video bg-zinc-800/50 relative cursor-pointer"
                  onClick={() => openProject(project.id)}
                >
                  {/* Mock Waveform Preview */}
                  <div className="absolute inset-4 flex flex-col justify-center gap-2">
                    {(project.tracks || []).slice(0, 4).map((track, i) => (
                      <div 
                        key={i}
                        className={`h-4 rounded bg-gradient-to-r opacity-60 ${
                          ['from-cyan-500/60 to-blue-500/60', 'from-purple-500/60 to-pink-500/60', 'from-amber-500/60 to-orange-500/60', 'from-emerald-500/60 to-teal-500/60'][i % 4]
                        }`}
                        style={{ width: `${40 + i * 15}%` }}
                      />
                    ))}
                    {(!project.tracks || project.tracks.length === 0) && (
                      <div className="flex items-center justify-center h-full text-zinc-600">
                        <Music className="w-8 h-8" />
                      </div>
                    )}
                  </div>

                  {/* Play Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-indigo-500 flex items-center justify-center shadow-lg">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate mb-1">{project.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Music className="w-3 h-3" />
                          {project.tracks?.length || 0} tracks
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(project.updated_at || project.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 text-zinc-500 hover:text-white"
                          data-testid={`project-menu-${project.id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                        <DropdownMenuItem 
                          onClick={() => openProject(project.id)}
                          className="cursor-pointer"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteProject(project.id)}
                          className="cursor-pointer text-red-400 focus:text-red-400"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!isLoading && searchQuery && filteredProjects.length === 0 && projects.length > 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">No projects match &quot;{searchQuery}&quot;</p>
          </div>
        )}
      </main>
      
      {/* Template Selector Modal */}
      <ProjectTemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </div>
  );
};

export default ProjectsPage;
